/**
 * Whether saving the KPS Configuration tab should open the recalculation confirmation
 * modal (`intent.md` FR-1, FR-1a, FR-1b).
 *
 * The recalculation-triggering set is exactly the 34 aliases `CALC_KPS_FOR_KEYWORDS` reads
 * out of the config object -- pinned server-side by
 * `backend/app/Constants/KpsRecalculationTriggers.php` and derived mechanically from
 * `backend/database/snowflake/06_calc_kps_for_keywords.sql`
 * (`decisions/kps-trigger-aliases-come-from-the-procedure.md`).
 *
 * `stateToSettings` (`./utils.ts`) sends **37** aliases. This file holds the **three** it
 * sends that the procedure never reads -- an EXCLUSION list, not the 34-name inclusion
 * list, per the same decision doc:
 *
 * - It is reviewable by eye at three names; a 34-name copy invites a silent typo.
 * - The two lists are deliberately asymmetric: the server list decides whether a run
 *   happens, this one decides only whether a modal is shown, so a drift between them
 *   produces a visible, cheap failure (a `422` from `POST /api/kps-recalculation`), never
 *   an invisible one.
 *
 * Do NOT derive this list from `constants.ts § ALIAS_META` -- that map labels Change
 * History rows and covers aliases this tab never sends. Do not take `stateToSettings`'s
 * list and subtract the exclusions from memory; that is how a 36-vs-34 error got into the
 * first draft of this spec.
 */
export const RECALCULATION_EXCLUDED_ALIASES: ReadonlySet<string> = new Set([
  // "Hero Embedding Components" (RelevanceScoringCard). Read by ComputeHeroRelevance and
  // EnrichKeywordBankKeywords to assemble the text that gets embedded (BR34) -- never by
  // CALC_KPS_FOR_KEYWORDS. Rescoring KPS against unchanged embeddings would not reflect a
  // change to it (FR-1a).
  "hero_embeddings_components",
  // "Search Trend Weight" (SearchTrendScoringCard). The trend contribution comes from the
  // tier points; the reported maximum is GREATEST(:p1..:p5) in the procedure, not this
  // alias. Read only by `kpsScoringConfig()` (the tooltip's displayed max) and
  // `26_landscape_factor_availability.sql`, neither of which writes `landscape_keywords`
  // (FR-1b).
  "kps_search_trend_score_max",
  // AUT-496: SERP Match's cutoff multiplier and anchor size. CALC_KPS_FOR_KEYWORDS never reads
  // either -- SERP Match does not feed KPS in this version -- so a change to them must not offer
  // a rescore of every stored score.
  "serp_match_cutoff_multiplier",
  "serp_match_anchor_top_n",
  // Search Trend tier-5 max bound (SearchTrendScoringCard, rendered disabled). No
  // counterpart in the procedure at all -- tier 5 is the open-ended `trend > t4b` branch,
  // so a fifth bound is structurally unused (FR-1b).
  "kps_search_trend_score_tier5_max_bound",
]);

/**
 * True iff at least one alias present in either payload -- other than the three excluded
 * above -- has a different value between `current` (what Save is about to send) and
 * `loaded` (the values the tab loaded when the form was populated; never the hardcoded
 * defaults).
 */
export function hasTriggeringChange(
  current: { alias: string; value: string }[],
  loaded: Record<string, string>,
): boolean {
  const currentMap = new Map(current.map((setting) => [setting.alias, setting.value]));
  const aliases = new Set<string>([...currentMap.keys(), ...Object.keys(loaded)]);
  for (const alias of aliases) {
    if (RECALCULATION_EXCLUDED_ALIASES.has(alias)) continue;
    if (currentMap.get(alias) !== loaded[alias]) return true;
  }
  return false;
}
